import { Poll, PollStatus } from '@tribeplatform/gql-client/types'
import { usePoll } from '@tribeplatform/react-sdk/hooks'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { T } from '../i18n/index.js'
import { cn } from '../utils/cn.js'

import { formatDate, percentage } from './utils.js'

const DOT = '·'

export function PollResult({ id }: { id: Poll['id'] }) {
  const poll = usePoll(id)

  const userSelections = poll.usersVote?.selectionIds

  return (
    <div className="flex flex-col">
      <List spacing="sm" className="flex flex-col gap-2 !p-0 !m-0">
        {poll?.options.map(({ id, label, result }) => {
          const isUserChoice = userSelections?.includes(id)
          return (
            <List.Item
              key={id}
              className={cn(
                '!p-4 !m-0 border border-line-subdued rounded-md relative flex items-center justify-between overflow-hidden gap-2',
                isUserChoice && 'border-interactive',
              )}
            >
              {isUserChoice && (
                <div className="absolute top-0 left-0 h-full bg-action-primary opacity-10 w-full" />
              )}
              {result && (
                <div
                  style={{ width: `${percentage(result.voteCastShare)}` }}
                  className={cn(
                    'absolute top-0 left-0 h-full z-1',
                    isUserChoice
                      ? 'bg-action-primary opacity-20'
                      : 'bg-surface-subdued opacity-70',
                  )}
                />
              )}
              <div className="flex items-center gap-2 z-10">{label}</div>
              {result && (
                <div className="flex gap-2 items-center text-xs shrink-0">
                  <span>{percentage(result.voteCastShare)}</span>
                  <span>{DOT}</span>
                  <SvgIcon name="member" />
                  <span className={cn('font-bold')}>{result.voteCount}</span>
                </div>
              )}
            </List.Item>
          )
        })}
      </List>
      <Divider />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TotalParticipants poll={poll} />
          {poll.maxSelections !== 1 && (
            <>
              {DOT}
              <TotalVotes poll={poll} />
            </>
          )}
        </div>
        {poll.status === PollStatus.Closed && (
          <Tooltip>
            <Tooltip.Trigger>
              <span className="text-sm">
                <T id="Poll.PollHasEnded" defaultMessage="Poll has ended" />
              </span>
            </Tooltip.Trigger>
            <Tooltip.Panel>{formatDate(new Date(poll.closesAt))}</Tooltip.Panel>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

function TotalParticipants({ poll }: { poll: Poll }) {
  if (!poll.totalParticipants && poll.totalParticipants !== 0) {
    return null
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <SvgIcon name="members" />
      <T
        id="Poll.TotalParticipants"
        defaultMessage="Total participants: {totalParticipants}"
        values={{ totalParticipants: poll.totalParticipants }}
      />
    </div>
  )
}

function TotalVotes({ poll }: { poll: Poll }) {
  if (!poll.totalVoteCasts && poll.totalVoteCasts !== 0) {
    return null
  }

  return (
    <span className="flex items-center gap-2 text-sm">
      <SvgIcon name="analytics" />
      <T
        id="Poll.TotalVotes"
        defaultMessage="Total votes: {totalVotes}"
        values={{ totalVotes: poll.totalVoteCasts }}
      />
    </span>
  )
}
